import React, { useCallback, useEffect, useMemo } from 'react'
import { Trans } from 'react-i18next'
import { useBoolean } from 'react-use'
import { createEditor, Descendant, Editor, Point, Range, Element as SlateElement, Transforms } from 'slate'
import { ReactEditor, RenderElementProps, useSlateStatic, withReact } from 'slate-react'
import { v4 as uuidv4 } from 'uuid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  VARIABLE_TEXT_ALL_VALUES,
  VARIABLE_TEXT_FEATURE_FLAG_ALL,
  VariableResultActionOutput,
  VariableResultColumnData,
  VariableResultCommon,
  VariableResultCurrentListItem,
  VariableResultCurrentRow,
  VariableResultGlobalVariable,
  VariableResultIcon,
  VariableResultProfileData,
  VariableResultSingleCell,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { ArrowShortRight16, Column16, Date16, Db16, SingleCell16, Table16, User16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Chip, ChipProps, Tooltip } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { getDefaultDescendantValue, SlateEditor } from '@/lib/slate'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { appIconKeys } from '@/shared/icons'

import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { RenderColumn } from '../RenderColumn'
import { useVariableTextContext, VariableTextContext } from './VariableTextContext'
import { VariableTextDropdown } from './VariableTextDropdown'

const VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT = [...VARIABLE_TEXT_ALL_VALUES, VariableTextFeatureFlag.ActionOutput]

export const withVariableTextEditor = (editor: ReactEditor) => {
  const { deleteBackward, isVoid, isInline, isElementReadOnly, isSelectable, apply } = editor

  // Keep track of the original apply function
  editor.apply = (operation) => {
    // Allow all selection operations to pass through normally
    if (operation.type === 'set_selection') {
      return apply(operation)
    }

    // For non-selection operations, check if we're at a variable text node
    if (editor.selection) {
      const [currentNode] = Editor.node(editor, editor.selection)
      const isVariableText =
        SlateElement.isElement(currentNode) &&
        VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(currentNode.type))

      // If we're at a variable text node and trying to move the cursor,
      // handle it specially
      if (isVariableText && operation.type === 'move_node') {
        const [node] = Editor.node(editor, operation.path)
        if (SlateElement.isElement(node) && VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(node.type))) {
          return // Prevent moving within variable text nodes
        }
      }
    }

    // Apply all other operations normally
    apply(operation)
  }

  editor.isInline = (element) =>
    VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(element.type)) || isInline(element)

  editor.isElementReadOnly = (element) =>
    VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(element.type)) || isElementReadOnly(element)

  editor.isSelectable = (element) => {
    // Allow selection for all elements, but maintain custom behavior
    if (VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(element.type))) {
      return true // Make variable text always selectable
    }
    return isSelectable(element)
  }

  editor.isVoid = (element) => {
    return VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(element.type)) || isVoid(element)
  }

  editor.deleteBackward = (...args) => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const [match] = Editor.nodes(editor, {
        match: (n) =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(n.type)),
      })

      if (match) {
        const [, path] = match
        const start = Editor.start(editor, path)

        if (Point.equals(selection.anchor, start)) {
          const newProperties: Partial<SlateElement> = {
            type: 'paragraph',
          }
          Transforms.setNodes(editor, newProperties, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              VARIABLE_TEXT_ALL_VALUES_WITH_ACTION_OUTPUT.includes(Number(n.type)),
          })
          return
        }
      }
    }

    deleteBackward(...args)
  }

  // Add methods to help with cursor movement
  const getNodeEdges = (editor: Editor, path: number[]): { start: Point; end: Point } => {
    const start = Editor.start(editor, path)
    const end = Editor.end(editor, path)
    return { start, end }
  }

  // Helper to check if we can move in a direction
  // @ts-expect-error
  editor.canMoveInDirection = (forward: boolean): boolean => {
    if (!editor.selection) return false

    const { anchor } = editor.selection
    const [node, path] = Editor.node(editor, anchor.path)

    if (SlateElement.isElement(node)) {
      const { start, end } = getNodeEdges(editor, path)
      if (forward) {
        return !Point.equals(anchor, end)
      } else {
        return !Point.equals(anchor, start)
      }
    }

    return true
  }

  return editor
}

interface VariableTextProps {
  id?: string
  icon?: React.ReactNode
  featureFlags?: VariableTextFeatureFlag
  onChange: (value: Descendant[]) => void
  value?: Descendant[]
  placeholder?: string
  forceUserSpecific?: boolean
  variableColumnOptions?: {
    columnTypes: ColumnTypes[]
  }
  validation?: (value: Descendant[]) => void
  tableId?: string
  settingId?: string
  isTextArea?: boolean
  options?: {
    id: string
    label: string
    description?: string
  }[]
  style?: React.CSSProperties
}

export function VariableTextV2({
  id,
  icon,
  featureFlags = VARIABLE_TEXT_FEATURE_FLAG_ALL,
  value = getDefaultDescendantValue(''),
  placeholder,
  onChange,
  forceUserSpecific = false,
  variableColumnOptions = {
    columnTypes: Object.values(ColumnTypes),
  },
  validation,
  tableId,
  settingId,
  isTextArea,
  options,
  style,
}: VariableTextProps) {
  const [open, setOpen] = useBoolean(false)

  const editorId = useMemo(() => settingId || id || uuidv4(), [settingId, id])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editor = useMemo(() => withVariableTextEditor(withReact(createEditor())), [editorId])

  const onEditorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!editor.selection) return

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const forward = event.key === 'ArrowRight'

      // Check if we can move in the desired direction
      // @ts-expect-error
      if (editor.canMoveInDirection(forward)) {
        // Let the default behavior handle it
        return
      } else {
        // Prevent moving if we're at a boundary of a variable text node
        event.preventDefault()
      }
    }
  }

  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, [])

  useEffect(() => {
    validation?.(value)
  }, [validation, value])

  return (
    <VariableTextContext.Provider
      key={editorId}
      value={{
        id,
        open,
        setOpen,
        icon,
        onChange,
        value,
        featureFlags,
        forceUserSpecific,
        variableColumnOptions,
        editor,
        validation,
        tableId,
        options,
        editorId,
      }}
    >
      <div className='mk-var-text'>
        <div className='mk-var-text-editor w-full outline-none word-wrap: break-word word-break: break-all mt-0.5'>
          <SlateEditor
            id={editorId}
            placeholder={placeholder}
            editor={editor}
            value={value}
            onChange={onChange}
            renderElement={renderElement}
            onKeyDown={onEditorKeyDown}
            style={isTextArea ? { minHeight: '85px' } : { minHeight: '44px', ...style }}
          />
        </div>
        {(featureFlags as number) !== 0 && <VariableTextDropdown />}
      </div>
    </VariableTextContext.Provider>
  )
}

/**
 * Variable Text Element
 *
 * @note watch out for this issue: https://github.com/ianstormtaylor/slate/issues/3421#issuecomment-591259117
 * - basically `contentEditable=false` needs to pair with `style={{ userSelect: 'none' }}`
 */
const Element = (props: RenderElementProps) => {
  const { db, tables, tablesMap } = useDbClientContext()
  const dbObj = db

  const { activeLayerTableId } = useActiveLayerContext()
  const { tableId } = useVariableTextContext()

  const editor = useSlateStatic()

  const { attributes, children, element } = props
  const path = ReactEditor.findPath(editor, element)

  const cls = 'inline-flex items-center leading-none'
  const chipProps = {
    defaultSize: false,
    className: 'text-m-sm min-h-[1.5rem] my-0.5 relative',
    onRemove: () => {
      Transforms.removeNodes(editor, { at: path })
    },
  } as ChipProps

  const IconWrap = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return <span className={cn('h-full w-6 min-h-0 absolute items-center flex-cc', className)}>{children}</span>
  }

  switch (Number(element.type)) {
    case VariableTextFeatureFlag.PageTitle:
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>Page Name</Chip>
          </span>
        </span>
      )
    case VariableTextFeatureFlag.PageLabel:
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>Page Title</Chip>
          </span>
        </span>
      )
    case VariableTextFeatureFlag.PageSubtitle:
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>Page Subtitle</Chip>
          </span>
        </span>
      )
    case VariableTextFeatureFlag.PageDescription:
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>Page Description</Chip>
          </span>
        </span>
      )
    case VariableTextFeatureFlag.PageCoverImage:
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>Page Cover Image</Chip>
          </span>
        </span>
      )
    case VariableTextFeatureFlag.ColumnData: {
      const el = element as unknown as VariableResultColumnData
      const tblObj = tables.find(({ id }) => id === el.tbl)
      const colObj = tblObj?.columns.find((c) => c.id === el.col)

      return (
        <span {...attributes} className={cls}>
          {children}
          <Tooltip
            content={
              <div>
                <div className='text-center pb-2'>
                  <Trans>Column Data</Trans>
                </div>
                <div className='flex-c'>
                  <div className='flex-c'>
                    <Db16 />
                    {dbObj?.name}
                  </div>
                  {tblObj && (
                    <>
                      <ArrowShortRight16 />
                      <div className='flex-c'>
                        <Table16 />
                        {tblObj.name}
                      </div>
                    </>
                  )}
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <div className='flex-c'>
                        <ColumnTypeIcon type={colObj.type} />
                        {colObj.name}
                      </div>
                    </>
                  )}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <IconWrap>{colObj ? <ColumnTypeIcon type={colObj?.type} /> : <Column16 />}</IconWrap>
                <div className='ltr:pl-7 rtl:pr-7'>{colObj?.name}</div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.CurrentRow: {
      const el = element as unknown as VariableResultCurrentRow
      const tblObj = tables.find(({ id }) => id === activeLayerTableId)
      const colObj = tblObj?.columns.find((c) => c.id === el.col)

      return (
        <span {...attributes} className={cls}>
          {children}
          <Tooltip
            content={
              <div>
                <div className='text-center pb-2'>
                  <Trans>Current Row</Trans>
                </div>
                <div className='flex-c'>
                  <div className='flex-c'>
                    <Db16 />
                    {dbObj?.name}
                  </div>
                  {tblObj && (
                    <>
                      <ArrowShortRight16 />
                      <div className='flex-c'>
                        <Table16 />
                        {tblObj.name}
                      </div>
                    </>
                  )}
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <RenderColumn withStyle={false} col={colObj} />
                    </>
                  )}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <IconWrap>
                  <SingleCell16 width={16} height={16} />
                </IconWrap>
                <div className='ltr:pl-7 rtl:pr-7'>{colObj?.name}</div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.Column:
    case VariableTextFeatureFlag.CurrentListItem: {
      const el = element as unknown as VariableResultCurrentListItem
      const tblObj = tables.find(({ id }) => id === tableId)
      const colObj = tblObj?.columns.find((c) => c.id === el.col)

      return (
        <span {...attributes} className={cls}>
          {children}
          <Tooltip
            content={
              <div>
                <div className='text-center pb-2'>
                  <Trans>Current List Item</Trans>
                </div>
                <div className='flex-c'>
                  <div className='flex-c'>
                    <Db16 />
                    {dbObj?.name}
                  </div>
                  {tblObj && (
                    <>
                      <ArrowShortRight16 />
                      <div className='flex-c'>
                        <Table16 />
                        {tblObj.name}
                      </div>
                    </>
                  )}
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <RenderColumn withStyle={false} col={colObj} />
                    </>
                  )}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <IconWrap>
                  <SingleCell16 width={16} height={16} />
                </IconWrap>
                <div className='ltr:pl-7 rtl:pr-7'>{colObj?.name}</div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.SingleCell: {
      const el = element as unknown as VariableResultSingleCell
      const tblObj = tables.find(({ id }) => id === el.tbl)
      const colObj = tblObj?.columns.find((c) => c.id === el.col)

      return (
        <span {...attributes} className={cls}>
          {children}

          <Tooltip
            content={
              <div>
                <div className='text-center pb-2'>
                  <Trans>Single Cell Value</Trans>
                </div>
                <div className='flex-c'>
                  <div className='flex-c'>
                    <Db16 />
                    {dbObj?.name}
                  </div>
                  {tblObj && (
                    <>
                      <ArrowShortRight16 />
                      <div className='flex-c'>
                        <Table16 />
                        {tblObj.name}
                      </div>
                    </>
                  )}
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <RenderColumn withStyle={false} col={colObj} />
                    </>
                  )}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <IconWrap>
                  <SingleCell16 width={16} height={16} />
                </IconWrap>
                <div className='ltr:pl-7 rtl:pr-7'>{colObj?.name}</div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.DateTime: {
      //const el = element as unknown as VariableResultDateTime
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <Date16 />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>
                <Trans>Current Date & Time</Trans>
              </span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.Icon: {
      const el = element as unknown as VariableResultIcon
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <MaterialIcon size={16} icon={el.value} />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>&nbsp;</span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.ProfileData: {
      const el = element as unknown as VariableResultProfileData
      const tbl = tablesMap.get(window.appUserTableID)
      const colObj = el.col ? tbl?.columnsMap.get(el.col) : undefined

      const invalidUserTable = window.appUserTableID !== tbl?.id || !colObj

      return (
        <span {...attributes} className={cls}>
          {children}

          <Tooltip
            content={
              invalidUserTable ? (
                <div className='flex-c red'>
                  <MaterialIcon size={18} icon='error' fill />
                  <Trans>Invalid profile data due to user table change.</Trans>
                </div>
              ) : (
                <div className='flex-c'>
                  <div className='flex-c'>
                    <User16 />
                    Profile Data
                  </div>
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <RenderColumn
                        withStyle={false}
                        col={{
                          id: colObj._id,
                          name: colObj._Name,
                          type: colObj._Type,
                        }}
                      />
                    </>
                  )}
                </div>
              )
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                {invalidUserTable && <MaterialIcon size={16} fill icon='error' className='red' />}
                <User16 />
                <div>{colObj?._Name}</div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.SessionID: {
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <MaterialIcon size={14} icon={appIconKeys.tableSession} />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>
                <Trans>Session ID</Trans>
              </span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.UniqueID: {
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <MaterialIcon size={14} icon={appIconKeys.uniqueId} />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>
                <Trans>Unique Identifier</Trans>
              </span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.NewsletterEmail: {
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <MaterialIcon size={14} icon={appIconKeys.newsletterEmail} />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>
                <Trans>Newsletter Email</Trans>
              </span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.Image: {
      const el = element as unknown as VariableResultCommon
      return (
        <span {...attributes} className={cls}>
          {children}
          <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
            <Chip {...chipProps}>
              <IconWrap>
                <img src={el.value} className='w-[18px] h-[18px] object-cover' />
              </IconWrap>
              <span className='ltr:pl-6 rtl:pr-6'>&nbsp;</span>
            </Chip>
          </span>
        </span>
      )
    }
    case VariableTextFeatureFlag.ActionOutput: {
      const el = element as unknown as VariableResultActionOutput
      const tblObj = tables.find(({ id }) => id === el.value.tableId)
      const colObj = tblObj?.columns.find((c) => c.id === el.value.colId)

      return (
        <span {...attributes} className={cls}>
          {children}

          <Tooltip
            content={
              <div>
                <div className='text-center pb-2'>
                  <Trans>Node Output</Trans>
                </div>
                <div className='flex-c'>
                  {tblObj && (
                    <div className='flex-c'>
                      <Table16 />
                      {tblObj.name}
                    </div>
                  )}
                  {colObj && (
                    <>
                      <ArrowShortRight16 />
                      <RenderColumn withStyle={false} col={colObj} />
                    </>
                  )}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <div className='border-r pr-2'>
                  <MaterialIcon size={14} icon={appIconKeys.actionOutput} />
                </div>
                <div className=''>
                  <RenderColumn withStyle={false} col={colObj} />
                </div>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    case VariableTextFeatureFlag.GlobalVariable: {
      const el = element as unknown as VariableResultGlobalVariable
      return (
        <span {...attributes} className={cls}>
          {children}

          <Tooltip
            content={
              <div className='flex-c'>
                <div className='flex-c'>
                  <MaterialIcon size={16} icon={appIconKeys.globalVariable} />
                  <Trans>Global Variable</Trans>
                </div>

                <ArrowShortRight16 />

                <div className='flex-c'>
                  <MaterialIcon size={16} icon={appIconKeys.storage} />
                  {el.value}
                </div>
              </div>
            }
          >
            <span style={{ userSelect: 'none' }} contentEditable={false} className='box-border px-0.5'>
              <Chip {...chipProps}>
                <span className='h-full min-h-0 absolute items-center flex-cc'>
                  <MaterialIcon size={16} icon={appIconKeys.globalVariable} />
                </span>
                <span className='ltr:pl-6 rtl:pr-6'>{el.value}</span>
              </Chip>
            </span>
          </Tooltip>
        </span>
      )
    }
    default:
      return <div {...attributes}>{children}</div>
  }
}
